import React from 'react';
import upperFirst from 'lodash/fp/upperFirst';
import { ClassValue } from 'classnames/types';
import Element, { SupportedColor } from '../Element/Element';
import styles from './Icon.module.scss';
import iconData from './iconData';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import Div from '../Element/Div/Div';
import { SupportedIconName } from '../../shared/icons';
import { CreateProps } from '../../types/utils/CreateProps';
import Link from '../Link/Link';

// Expose for external use as well
export * from '../../shared/icons';

export const supportedIconSizes = ['xs', 'sm', 'md', 'lg', 'xlg'] as const;
export type SupportedIconSize = ArrayElementType<typeof supportedIconSizes>;

// Aliases for backwards compatibility
export type IconName = SupportedIconName;
export type IconSize = SupportedIconSize;

export type IconProps = CreateProps<
  {
    /** Name of icon */
    name: SupportedIconName;
    /** Icon size */
    size: SupportedIconSize;
    /** Icon color */
    color?: SupportedColor;
    svgFocusable?: boolean;
    tagName?: keyof JSX.IntrinsicElements;
    url?: string;
  },
  typeof Div,
  'tagName'
>;
// Alias for backwards compatibility
export type IconAttributes = IconProps;

const Icon: React.FunctionComponent<IconProps> = ({
  name,
  size,
  url,
  className,
  color,
  svgFocusable,
  tagName = 'div',
  ...props
}) => {
  const IconElement = iconData[name];

  if (!IconElement) {
    console.error(`Unfortunately there's no icon with a name "${name}"`);
    return null;
  }

  const classNames: ClassValue = [
    styles.icon,
    `telia_icon__${name}`,
    styles[`icon__size${upperFirst(size)}`],
  ];
  className && classNames.push(className);

  return (
    <Element className={classNames} tagName={tagName} color={color} {...props}>
      {url ? (
        <Link color={color} hoverColor={color} to={url}>
          <IconElement
            focusable={svgFocusable}
            className={styles['icon_svg']}
          />
        </Link>
      ) : (
        <IconElement focusable={svgFocusable} className={styles['icon_svg']} />
      )}
    </Element>
  );
};

export default Icon;
